import { gql } from '@apollo/client/core';
import { CRM_STAGE_FIELDS } from '../fragments/crmStage';

export const GET_ORGANIZATIONS = gql`
  query GetOrganizations(
    $limit: Int!
    $offset: Int!
    $search: String
    $activeOnly: Boolean!
    $sortBy: String
    $sortOrder: String!
  ) {
    organizations(
      limit: $limit
      offset: $offset
      search: $search
      activeOnly: $activeOnly
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      organizations {
        organizationId
        orgName
        orgPhone
        active
        completedSetup
        acceptedTerms
        activeSubscription
        beta
        email
        notificationEmail
        timezone
        domain
        assistantName
        assistantNamePronunciation
        clientAddress
        primaryContact
        subscription
        callSettings
        phoneNumbers
        branding
        logoUrl
        combinedName
        subdomain
        calendlyUrl
        createdAt
        updatedAt
        twilioSecretRef
        syncLink
        # stripeAccountId   # unused in frontend
        # stripeCustomerId   # unused in frontend
        connectedCrm
        isOnboardingComplete
        activeClient
        externalClient
        emailCampaignsEnabled
        dnsConfigured
        smsAllOutcomes
        orgLinks
        fubSetup
        hubspot
        onboardingSetup
        onboardingData
        settings
        sms
        rolloutConfig
        remiIntroName
        cincSetup
        oauthSetup
        lastInitialSync
        databaseUploads
        integrationSetupRequests
        databaseSize
        brokerageName
        clientOwner
        emailConfig {
          organizationId
          # sendgridSenderId       # unused in frontend
          sendgridSenderEmail
          # sendgridDomainId       # unused in frontend
          sendgridReplyToEmail
          sendgridReplyToName
          # sendgridFromName       # unused in frontend
          # emailSetup             # temporarily excluded — backend returns unserialised dict for some orgs
          # requiredDnsRecords     # unused in frontend
          # dnsValidatedAt         # unused in frontend
          # emailSetupDate         # unused in frontend
          # emailMethod            # unused in frontend
          # senderPrefix           # unused in frontend
          # dnsValidationDetails   # unused in frontend
          # createdAt              # unused in frontend
          # updatedAt              # unused in frontend
        }
      }
      total
      hasMore
    }
  }
`;

export const GET_ORGANIZATIONS_SWITCHER = gql`
  query GetOrganizationsSwitcher($limit: Int!, $offset: Int!, $search: String) {
    organizations(
      limit: $limit
      offset: $offset
      search: $search
      activeOnly: false
      sortBy: "orgName"
      sortOrder: "ASC"
    ) {
      organizations {
        organizationId
        orgName
      }
      total
      hasMore
    }
  }
`;

export const GET_ORGANIZATION = gql`
  query GetOrganization($organizationId: String!) {
    organization(organizationId: $organizationId) {
      organizationId
      orgName
      orgPhone
      active
      completedSetup
      acceptedTerms
      activeSubscription
      beta
      email
      notificationEmail
      timezone
      domain
      assistantName
      assistantNamePronunciation
      clientAddress
      primaryContact
      subscription
      callSettings
      phoneNumbers
      branding
      logoUrl
      combinedName
      subdomain
      calendlyUrl
      createdAt
      updatedAt
      twilioSecretRef
      syncLink
      # stripeAccountId   # unused in frontend
      # stripeCustomerId   # unused in frontend
      connectedCrm
      isOnboardingComplete
      activeClient
      externalClient
      emailCampaignsEnabled
      dnsConfigured
      smsAllOutcomes
      orgLinks
      fubSetup
      hubspot
      onboardingSetup
      onboardingData
      settings
      sms
      rolloutConfig
      remiIntroName
      cincSetup
      oauthSetup
      lastInitialSync
      databaseUploads
      integrationSetupRequests
      databaseSize
      brokerageName
      clientOwner
      crmConnections {
        connectionId
        organizationId
        crmType
        app
        status
        syncEnabled
        syncFilterTag
        initialSync
        connectedAt
        disconnectedAt
        disconnectionReason
        createdAt
        updatedAt
      }
      users {
        userId
        email
        firstName
        lastName
        phone
        photoUrl
        role
        status
        isActive
        # invitedBy   # unused in frontend
        # invitedAt   # unused in frontend
        # acceptedAt  # unused in frontend
        availableForCalls
        availableForTransfers
        # createdAt   # unused in frontend
      }
      emailConfig {
        organizationId
        # sendgridSenderId       # unused in frontend
        sendgridSenderEmail
        # sendgridDomainId       # unused in frontend
        sendgridReplyToEmail
        sendgridReplyToName
        # sendgridFromName       # unused in frontend
        # emailSetup             # temporarily excluded — backend returns unserialised dict for some orgs
        # requiredDnsRecords     # unused in frontend
        # dnsValidatedAt         # unused in frontend
        # emailSetupDate         # unused in frontend
        # emailMethod            # unused in frontend
        # senderPrefix           # unused in frontend
        # dnsValidationDetails   # unused in frontend
        # createdAt              # unused in frontend
        # updatedAt              # unused in frontend
      }
    }
  }
`;

/**
 * Grouped SMS conversation threads for the org (SPEC-1880). One row per
 * conversation (collapses messages by COALESCE(conversation_id, message_id))
 * so the SMS inbox shows "Jane Doe · 12 texts · latest preview" instead of
 * 12 individual rows. Standalone texts (no conversation_id) come back with
 * `isStandalone: true` and a synthetic conversation_id = message_id so the
 * drill-down via `messages(conversationId:)` still opens them.
 */
/**
 * Per SPEC-1899/1906/1907/1908 (Suleman #2448):
 *   - SMS now groups by COALESCE(contact_id, message_id), so one row
 *     per contact — same semantic as activityFeed/recentActivities/
 *     hotLeads. Contactless texts stay standalone, keyed by message_id.
 *   - `dateFrom`/`dateTo` filter messages before grouping → only
 *     threads with a message in range are returned.
 *   - With a range, `lastMessagePreview` / `lastMessageAt` /
 *     `lastDirection` reflect the latest matching message in range.
 *   - `matchedMessageId` is the drill-down scroll target: newest
 *     message_id in range when a date filter is active, else the
 *     thread's most-recent message_id.
 */
export const GET_ORG_CONVERSATION_THREADS = gql`
  ${CRM_STAGE_FIELDS}
  query GetOrgConversationThreads(
    $organizationId: String!
    $channel: String!
    $limit: Int!
    $offset: Int!
    $contactId: String
    $search: String
    $dateFrom: DateTime
    $dateTo: DateTime
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      conversationThreads(
        channel: $channel
        limit: $limit
        offset: $offset
        contactId: $contactId
        search: $search
        dateFrom: $dateFrom
        dateTo: $dateTo
      ) {
        items {
          conversationId
          contactId
          contactName
          contactPhone
          contactEmail
          channel
          messageCount
          unreadCount
          lastMessageAt
          lastMessagePreview
          lastDirection
          sentiment
          isStandalone
          matchedMessageId
          crmStages {
            ...CrmStageFields
          }
        }
        total
        hasMore
      }
    }
  }
`;

/**
 * Paginated org SMS list. Used for both the legacy flat-list drill-down
 * and the SPEC-1880 thread drill-down — pass `conversationId` to scope
 * messages to a single thread.
 */
export const GET_ORG_SMS_MESSAGES = gql`
  query GetOrgSmsMessages(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $dateFrom: DateTime
    $dateTo: DateTime
    $search: String
    $conversationId: String
    $sortOrder: String
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      messages(
        channel: "sms"
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        search: $search
        conversationId: $conversationId
        sortBy: "created_at"
        sortOrder: $sortOrder
      ) {
        items {
          messageId
          contactId
          contactName
          direction
          body
          status
          sentAt
          createdAt
        }
        total
        hasMore
      }
    }
  }
`;

// SPEC-1707: hottest-first lead list for the dashboard Lead Activity widget.
export const GET_HOT_LEADS = gql`
  query GetHotLeads(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $scoreMin: Int
    $scoreMax: Int
    $orderBy: String
    $search: String
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      hotLeads(
        limit: $limit
        offset: $offset
        scoreMin: $scoreMin
        scoreMax: $scoreMax
        orderBy: $orderBy
        search: $search
      ) {
        items {
          contactId
          name
          phone
          currentScore
          previousScore
          touchCounts {
            total
            calls
            sms
            emails
          }
          lastFromLead {
            channel
            direction
            occurredAt
            preview
            outcome
            sentiment
            durationSeconds
            callId
            messageId
            summary
          }
          lastFromRemi {
            channel
            direction
            occurredAt
            preview
            outcome
            sentiment
            durationSeconds
            callId
            messageId
            summary
          }
          nextTouch {
            channel
            scheduledAt
            reason
          }
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_ORG_FLOW_NAMES = gql`
  query GetOrgFlowNames($organizationId: String!) {
    organization(organizationId: $organizationId) {
      flows {
        flowId
        name
      }
    }
  }
`;

export const GET_CONVERSATION_THREADS = gql`
  query GetConversationThreads(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $channel: String
    $contactId: String
    $status: String
    $search: String
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      conversationThreads(
        limit: $limit
        offset: $offset
        channel: $channel
        contactId: $contactId
        status: $status
        search: $search
      ) {
        items {
          conversationId
          contactId
          contactName
          contactEmail
          contactPhone
          channel
          status
          subject
          fromAddress
          toAddress
          messageCount
          unreadCount
          lastMessageAt
          lastMessagePreview
          lastDirection
          sentiment
          priority
          tags
          assignedAgentId
          flowExecutionId
          metadata
          createdAt
          updatedAt
        }
        total
        hasMore
      }
    }
  }
`;

// --- Separate on-demand queries for heavy resolvers ---

export const GET_ORGANIZATION_FLOWS = gql`
  query GetOrganizationFlows($organizationId: String!) {
    organization(organizationId: $organizationId) {
      organizationId
      flows {
        flowId
        flowType
        name
        status
        rejoin
        timezone
        allowedHours
        allowedDays
        excludeHolidays
        countryCode
        transferNumbers
        useRoundRobin
        deletedAt
      }
    }
  }
`;

/**
 * Lean query for the Flywheel config — pulls only the org's Flywheel flow
 * via the server-side `flowType` filter, so we don't fetch every flow on
 * the org just to throw all but one row away.
 */
export const GET_FLYWHEEL_FLOW = gql`
  query GetFlywheelFlow($organizationId: String!) {
    flows(
      organizationId: $organizationId
      flowType: "Flywheel"
      limit: 1
      offset: 0
      sortOrder: "DESC"
    ) {
      flows {
        flowId
        flowType
        timezone
        allowedHours
        allowedDays
        excludeHolidays
        countryCode
        transferNumbers
        deletedAt
      }
    }
  }
`;

/**
 * Slim flow list for filter dropdowns. Returns only the 3 fields needed to
 * label and key an option — fetching the full GET_FLOWS payload just to
 * populate a `<Select>` was wasteful (25+ fields per flow, including
 * voicemail content and prompt parameters).
 */
export const GET_FLOW_OPTIONS = gql`
  query GetFlowOptions(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $sortBy: String
    $sortOrder: String!
  ) {
    flows(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      flows {
        flowId
        name
        flowType
      }
    }
  }
`;

export const GET_FLOWS = gql`
  query GetFlows(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $flowType: String
    $status: String
    $subType: String
    $search: String
    $sortBy: String
    $sortOrder: String!
    $scheduledAfter: DateTime
    $scheduledBefore: DateTime
  ) {
    flows(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      flowType: $flowType
      status: $status
      subType: $subType
      search: $search
      sortBy: $sortBy
      sortOrder: $sortOrder
      scheduledAfter: $scheduledAfter
      scheduledBefore: $scheduledBefore
    ) {
      flows {
        organizationId
        flowId
        name
        description
        flowType
        subType
        status
        scheduledFor
        timezone
        allowedHours
        allowedDays
        excludeHolidays
        countryCode
        scheduleOverUnit
        schedulingWindow
        promptParameters
        descriptionId
        transferNumbers
        useRoundRobin
        activeCount
        leadSource
        leadEmail
        voicemailContent
        createdAt
        deletedAt
        contactCount
        callCount
      }
      total
      hasMore
    }
  }
`;

export const GET_ORGANIZATION_AGENTS = gql`
  query GetOrganizationAgents(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $sortOrder: String!
    $sortBy: String
    $search: String
  ) {
    users(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      sortOrder: $sortOrder
      sortBy: $sortBy
      search: $search
    ) {
      users {
        userId
        firstName
        lastName
        email
        phone
        role
        status
        isActive
        photoUrl
        bio
        slug
        invitedAt
        acceptedAt
      }
      total
      hasMore
    }
  }
`;

export const GET_USER = gql`
  query GetUser($firebaseAuthId: String, $fresh: Boolean = false) {
    user(firebaseAuthId: $firebaseAuthId, fresh: $fresh) {
      userId
      email
      firstName
      lastName
      phone
      photoUrl
      role
      status
      primaryOrganizationId
      preferences
      bio
      slug
      # acceptedAt           # unused — not mapped in useUser
      # invitedAt            # unused — not mapped in useUser
      # availableForCalls    # unused — not mapped in useUser
      # availableForTransfers # unused — not mapped in useUser
      # createdAt            # unused — not mapped in useUser
      # updatedAt            # unused — not mapped in useUser
      # lastLoginAt          # unused — not mapped in useUser
    }
  }
`;

export const GET_KNOWLEDGE_BASES = gql`
  query GetKnowledgeBases(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $kbType: String
    $flowType: String
    $search: String
    $sortBy: String
    $sortOrder: String!
  ) {
    knowledgeBases(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      kbType: $kbType
      flowType: $flowType
      search: $search
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      knowledgeBases {
        kbId
        kbName
        kbType
        knowledgeBase
        knowledgebaseText
        flowType
        organizationId
        createdAt
        updatedAt
      }
      total
      hasMore
    }
  }
`;

export const GET_FLOW_CONTACTS = gql`
  query GetFlowContacts(
    $organizationId: String!
    $flowId: String!
    $limit: Int
    $offset: Int
    $search: String
    $executionStatus: String
    $sortBy: String
    $sortOrder: String
  ) {
    flowContacts(
      organizationId: $organizationId
      flowId: $flowId
      limit: $limit
      offset: $offset
      search: $search
      executionStatus: $executionStatus
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      items {
        contactId
        firstName
        lastName
        email
        phone
        contactStatus
        lifecycleStage
        tags
        executionId
        executionStatus
        currentStep
        totalSteps
        nextScheduledAt
        flowCreatedAt
      }
      total
      hasMore
    }
  }
`;

export const GET_FLOW_CALLS = gql`
  query GetFlowCalls(
    $organizationId: String!
    $flowId: String!
    $limit: Int
    $offset: Int
    $outcome: String
    $search: String
    $sortBy: String
    $sortOrder: String
  ) {
    flowCalls(
      organizationId: $organizationId
      flowId: $flowId
      limit: $limit
      offset: $offset
      outcome: $outcome
      search: $search
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      items {
        callId
        contactId
        contactName
        assignedAgentId
        assignedAgent {
          agentId
          firstName
          lastName
          email
          phone
          photoUrl
        }
        conversationId
        direction
        inbound
        fromNumber
        toNumber
        status
        outcome
        answeredBy
        durationSeconds
        initiatedAt
        endedAt
        summary
        sentiment
        recordingUrl
        transcript
        agentId
        flowName
        flowType
        callAnalysisTimeline
        callAnalysisAnswers
        isTest
        createdAt
      }
      total
      hasMore
    }
  }
`;

// --- Split dashboard stat queries (replaces GET_DASHBOARD_STATS) ---
// Each query only runs when its corresponding widget is visible on the dashboard.

export const GET_DASHBOARD_COUNTS = gql`
  query GetDashboardCounts($organizationId: String!, $dateFrom: DateTime, $dateTo: DateTime) {
    organization(organizationId: $organizationId) {
      organizationId
      dashboardAggregates(dateFrom: $dateFrom, dateTo: $dateTo) {
        callsTotal
        conversationsTotal
        liveTransfersTotal
        opportunityCallsTotal
        smsTotal
        propertyViewsTotal
        leadCapturesTotal
        webConversionsTotal
        totalPageViews
        totalPropertyViews
        totalSearchPageViews
        totalHomeValuations
        totalSavedProperties
        totalEmailPropertyLinkViews
        totalWebsiteActivity
        emailDeliveredTotal
        emailOpensTotal
        emailClicksTotal
        allLeads
        emailLeads
        organicLeads
        ppcLeads
        # COMMENTED OUT — Active Users / Nurture / Search Alerts widgets (re-add later)
        # uniqueActiveUsers
        # uniqueContactsCallsAnswered
        # uniqueContactsEmailsOpened
        # uniqueContactsEmailsClicked
        # uniqueContactsPropertyClicked
        # uniqueContactsSearching
        # clientsInCallNurture
        # clientsInEmailNurture
        # totalSearchAlertsContacts
        # totalSearchesMadeByUsers
      }
      # LiveTransfers-primary count (COUNT FROM LiveTransfers) so the widget
      # matches the modal instead of the Calls-join in dashboardAggregates,
      # which misses transfers with a null original_call_id (SPEC-2172).
      stats(dateFrom: $dateFrom, dateTo: $dateTo) {
        totalLiveTransfers
      }
    }
  }
`;

export const GET_ACTIVE_USERS_WIDGET_STATS = gql`
  query GetActiveUsersWidgetStats(
    $organizationId: String!
    $dateFrom: DateTime
    $dateTo: DateTime
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      stats(dateFrom: $dateFrom, dateTo: $dateTo) {
        uniqueActiveUsers
        uniqueContactsCallsAnswered
        uniqueContactsEmailsOpened
        uniqueContactsEmailsClicked
        uniqueContactsPropertyClicked
        uniqueContactsSearching
      }
    }
  }
`;

export const GET_NURTURE_WIDGET_STATS = gql`
  query GetNurtureWidgetStats($organizationId: String!, $dateFrom: DateTime, $dateTo: DateTime) {
    organization(organizationId: $organizationId) {
      organizationId
      stats(dateFrom: $dateFrom, dateTo: $dateTo) {
        clientsInCallNurture
        clientsInEmailNurture
      }
    }
  }
`;

export const GET_SEARCH_ALERT_WIDGET_STATS = gql`
  query GetSearchAlertWidgetStats(
    $organizationId: String!
    $dateFrom: DateTime
    $dateTo: DateTime
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      stats(dateFrom: $dateFrom, dateTo: $dateTo) {
        totalSearchAlertsContacts
        totalSearchesMadeByUsers
      }
    }
  }
`;

// --- Legacy query kept for reference (no longer used by useDashboardStatsGQL) ---
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($organizationId: String!, $dateFrom: DateTime, $dateTo: DateTime) {
    organization(organizationId: $organizationId) {
      organizationId
      # Opportunities intentionally come from stats/opportunityCallsTotal.
      stats(dateFrom: $dateFrom, dateTo: $dateTo) {
        # totalContacts        # unused in dashboard
        # totalCalls           # unused — callsTotal comes from calls(limit:1) sub-query
        totalEmails
        # totalSms             # unused in dashboard
        # totalNotes           # unused in dashboard
        totalSearchAlertsContacts
        totalSearchesMadeByUsers
        totalPageViews
        totalPropertyViews
        totalSearchPageViews
        totalHomeValuations
        totalSavedProperties
        # totalEmailPropertyLinkViews      # unused in dashboard
        # totalWebsiteActivity             # unused in dashboard
        totalEmailOpens
        totalEmailClicks
        uniqueContactsCallsAnswered
        uniqueContactsEmailsOpened
        uniqueContactsEmailsClicked
        uniqueContactsPropertyClicked
        uniqueContactsSearching
        # uniqueContactsWithWebsiteActivity  # unused in dashboard
        # uniqueContactsWithConversions      # unused in dashboard
        uniqueActiveUsers
        # totalClientTouches               # unused in dashboard
        # totalClientTouchesCalls          # unused in dashboard
        # totalClientTouchesEmails         # unused in dashboard
        # totalClientTouchesWebsiteActivity # unused in dashboard
        # totalClientTouchesConversions    # unused in dashboard
        clientsInCallNurture
        clientsInEmailNurture
        # contactsUpdatedToday             # unused in dashboard
        # eventsProcessedToday             # unused in dashboard
        # contactsByStatus {               # unused in dashboard
        #   label
        #   count
        # }
        # contactsByLifecycleStage {       # unused in dashboard
        #   label
        #   count
        # }
        totalLiveTransfers
      }
      calls(limit: 1, offset: 0, dateFrom: $dateFrom, dateTo: $dateTo) {
        total
      }
      conversations: calls(
        limit: 1
        offset: 0
        dateFrom: $dateFrom
        dateTo: $dateTo
        answeredBy: "human"
      ) {
        total
      }
      messageEngagementGroups(limit: 1, offset: 0, dateFrom: $dateFrom, dateTo: $dateTo) {
        total
      }
      webActivities(
        limit: 1
        offset: 0
        dateFrom: $dateFrom
        dateTo: $dateTo
        eventTypes: ["property_view", "search", "lead_capture", "property_saved"]
      ) {
        total
      }
    }
  }
`;

export const GET_DASHBOARD_CONTACT_COUNT = gql`
  query GetDashboardContactCount(
    $organizationId: String!
    $dateFrom: DateTime
    $dateTo: DateTime
    $leadSource: String
    $lifecycleStage: String
  ) {
    contacts(
      organizationId: $organizationId
      limit: 1
      offset: 0
      dateFrom: $dateFrom
      dateTo: $dateTo
      leadSource: $leadSource
      lifecycleStage: $lifecycleStage
      sortOrder: "DESC"
    ) {
      total
    }
  }
`;

export const GET_DASHBOARD_MESSAGES_COUNT = gql`
  query GetDashboardMessagesCount(
    $organizationId: String!
    $dateFrom: DateTime
    $dateTo: DateTime
  ) {
    messages(
      organizationId: $organizationId
      limit: 1
      offset: 0
      dateFrom: $dateFrom
      dateTo: $dateTo
      channel: "email"
      sortOrder: "DESC"
    ) {
      total
    }
  }
`;

export const GET_DASHBOARD_WEB_ACTIVITIES_COUNT = gql`
  query GetDashboardWebActivitiesCount(
    $organizationId: String!
    $dateFrom: DateTime
    $dateTo: DateTime
  ) {
    webActivities(
      organizationId: $organizationId
      limit: 1
      offset: 0
      dateFrom: $dateFrom
      dateTo: $dateTo
      sortOrder: "DESC"
    ) {
      total
    }
  }
`;

export const GET_DASHBOARD_LEADS = gql`
  query GetDashboardLeads(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $optInDateFrom: DateTime
    $optInDateTo: DateTime
    $leadSources: [String!]
    $search: String
    $sortBy: String
    $sortOrder: String!
  ) {
    contacts(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      optInDateFrom: $optInDateFrom
      optInDateTo: $optInDateTo
      leadSources: $leadSources
      search: $search
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      contacts {
        contactId
        firstName
        lastName
        phone
        email
        leadSource
        leadSourceSecondary
        intentLevel
        leadIntent
        optInDate
        optInDesc
        createdAt
        valuationRequested
        assignedAgentId
        assignedPondId
        claimedLead
        claimedLeadSource
        assignedAgent {
          agentId
          firstName
          lastName
          email
          phone
          photoUrl
        }
      }
      total
      hasMore
    }
  }
`;

export const GET_DASHBOARD_LEAD_COUNTS = gql`
  query GetDashboardLeadCounts(
    $organizationId: String!
    $optInDateFrom: DateTime
    $optInDateTo: DateTime
  ) {
    emailLeads: contacts(
      organizationId: $organizationId
      limit: 1
      offset: 0
      leadSources: ["Email"]
      optInDateFrom: $optInDateFrom
      optInDateTo: $optInDateTo
      sortOrder: "DESC"
    ) {
      total
    }
    organicLeads: contacts(
      organizationId: $organizationId
      limit: 1
      offset: 0
      leadSources: ["Organic"]
      optInDateFrom: $optInDateFrom
      optInDateTo: $optInDateTo
      sortOrder: "DESC"
    ) {
      total
    }
    ppcLeads: contacts(
      organizationId: $organizationId
      limit: 1
      offset: 0
      leadSources: ["PPC"]
      optInDateFrom: $optInDateFrom
      optInDateTo: $optInDateTo
      sortOrder: "DESC"
    ) {
      total
    }
  }
`;

export const GET_ORG_CALLS_COUNT = gql`
  query GetOrgCallsCount(
    $organizationId: String!
    $dateFrom: DateTime
    $dateTo: DateTime
    $answeredBy: String
    $includeTest: Boolean
  ) {
    organization(organizationId: $organizationId) {
      calls(
        limit: 1
        offset: 0
        dateFrom: $dateFrom
        dateTo: $dateTo
        answeredBy: $answeredBy
        includeTest: $includeTest
      ) {
        total
      }
    }
  }
`;

export const GET_ORG_CALLS = gql`
  ${CRM_STAGE_FIELDS}
  query GetOrgCalls(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $dateFrom: DateTime
    $dateTo: DateTime
    $sortBy: String
    $sortOrder: String
    $answeredBy: String
    $includeTest: Boolean
    $hasTransfer: Boolean
    $search: String
    $flowName: String
    $flowIds: [String!]
    $flowTypes: [String!]
    $includeInbound: Boolean
    $outcomes: [String!]
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      calls(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        sortBy: $sortBy
        sortOrder: $sortOrder
        answeredBy: $answeredBy
        includeTest: $includeTest
        hasTransfer: $hasTransfer
        search: $search
        flowName: $flowName
        flowIds: $flowIds
        flowTypes: $flowTypes
        includeInbound: $includeInbound
        outcomes: $outcomes
      ) {
        items {
          callId
          conversationId
          contactId
          contactName
          assignedAgentId
          assignedAgent {
            agentId
            firstName
            lastName
            email
            phone
            photoUrl
          }
          direction
          inbound
          fromNumber
          toNumber
          status
          outcome
          answeredBy
          durationSeconds
          initiatedAt
          endedAt
          summary
          transcript
          sentiment
          recordingUrl
          flowName
          flowType
          isTest
          callAnalysisTimeline
          callAnalysisAnswers
          createdAt
          crmStages {
            ...CrmStageFields
          }
          transfer {
            transferId
            transferType
            status
            transferReason
            targetAgentId
            targetAgentNumber
            fromNumber
            toNumber
            connectedAt
            completedAt
            failureReason
            recordingUrl
            voicemailUrl
            initiatedAt
            createdAt
            answered
            acceptedBy
            transferSuccess
          }
        }
        total
        hasMore
      }
    }
  }
`;

// SPEC-2172: LiveTransfers-primary widget query. calls(hasTransfer: true)
// starts from Calls and drops any transfer whose originating-call linkage is
// missing (null originalCallId + transfer-leg SID). liveTransfers lists the
// transfer rows themselves; `call` is enrichment and may be null.
export const GET_ORG_LIVE_TRANSFERS = gql`
  query GetOrgLiveTransfers(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $dateFrom: DateTime
    $dateTo: DateTime
    $sortBy: String
    $sortOrder: String
    $search: String
    $contactId: String
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      liveTransfers(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        sortBy: $sortBy
        sortOrder: $sortOrder
        search: $search
        contactId: $contactId
      ) {
        items {
          contactId
          contactName
          contactPhone
          assignedAgentId
          assignedAgent {
            agentId
            firstName
            lastName
            email
            phone
            photoUrl
          }
          transfer {
            transferId
            transferType
            status
            transferReason
            targetAgentId
            targetAgentNumber
            fromNumber
            toNumber
            connectedAt
            completedAt
            failureReason
            recordingUrl
            voicemailUrl
            initiatedAt
            createdAt
            answered
            acceptedBy
            transferSuccess
          }
          call {
            callId
            conversationId
            direction
            inbound
            fromNumber
            toNumber
            status
            outcome
            answeredBy
            durationSeconds
            initiatedAt
            endedAt
            summary
            transcript
            sentiment
            recordingUrl
            flowName
            flowType
            isTest
            callAnalysisTimeline
            callAnalysisAnswers
            createdAt
          }
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_ORG_CALL_BY_ID = gql`
  query GetOrgCallById($organizationId: String!, $callId: String!) {
    organization(organizationId: $organizationId) {
      organizationId
      calls(callId: $callId, limit: 1, offset: 0) {
        items {
          callId
          conversationId
          contactId
          contactName
          assignedAgentId
          assignedAgent {
            agentId
            firstName
            lastName
            email
            phone
            photoUrl
          }
          direction
          inbound
          fromNumber
          toNumber
          status
          outcome
          answeredBy
          durationSeconds
          initiatedAt
          endedAt
          summary
          transcript
          sentiment
          recordingUrl
          flowName
          flowType
          isTest
          callAnalysisTimeline
          callAnalysisAnswers
          createdAt
          transfer {
            transferId
            status
            transferReason
            targetAgentNumber
            failureReason
            recordingUrl
            voicemailUrl
            answered
            acceptedBy
          }
        }
      }
    }
  }
`;

export const GET_MESSAGES = gql`
  query GetMessages(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $search: String
    $emailLogEventTypes: [String!]
    $emailLogDateFrom: DateTime
    $emailLogDateTo: DateTime
  ) {
    messages(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      search: $search
      channel: "email"
      emailLogEventTypes: $emailLogEventTypes
      emailLogDateFrom: $emailLogDateFrom
      emailLogDateTo: $emailLogDateTo
    ) {
      total
      hasMore
      items {
        messageId
        contactId
        contactName
        subject
        bodyHtml
        status
        sentAt
      }
    }
  }
`;

export const GET_ORG_WEB_ACTIVITIES = gql`
  query GetOrgWebActivities(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $dateFrom: DateTime
    $dateTo: DateTime
    $sortOrder: String
    $eventTypes: [String!]
    $deviceType: String
    $isGuest: Boolean
    $search: String
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      webActivities(
        limit: $limit
        offset: $offset
        dateFrom: $dateFrom
        dateTo: $dateTo
        sortOrder: $sortOrder
        eventTypes: $eventTypes
        deviceType: $deviceType
        isGuest: $isGuest
        search: $search
      ) {
        items {
          activityId
          contactId
          contactName
          eventType
          eventAction
          eventCategory
          pageUrl
          pageTitle
          referrer
          deviceType
          browser
          isGuest
          guestInfo
          properties
          timestamp
          createdAt
        }
        total
        hasMore
      }
    }
  }
`;

export const GET_ORG_RECENT_ACTIVITIES = gql`
  query GetOrgRecentActivities(
    $organizationId: String!
    $limit: Int
    $offset: Int
    $sortOrder: String
    $eventTypes: [String!]
    $channels: [String!]
  ) {
    organization(organizationId: $organizationId) {
      organizationId
      recentActivities(
        limit: $limit
        offset: $offset
        sortOrder: $sortOrder
        eventTypes: $eventTypes
        channels: $channels
      ) {
        items {
          eventId
          contactId
          contactName
          contactEmail
          contactPhone
          eventType
          eventSubtype
          channel
          summary
          outcome
          sentiment
          durationSeconds
          callId
          messageId
          sourceSystem
          metadata
          eventAt
          createdAt
        }
        hasMore
      }
    }
  }
`;

export const GET_FOLLOW_UP_DECISIONS = gql`
  query GetFollowUpDecisions(
    $organizationId: String!
    $limit: Int!
    $offset: Int!
    $decisionType: String
    $strategySegment: String
    $pendingOnly: Boolean!
  ) {
    followUpDecisions(
      organizationId: $organizationId
      limit: $limit
      offset: $offset
      decisionType: $decisionType
      strategySegment: $strategySegment
      pendingOnly: $pendingOnly
    ) {
      decisions {
        organizationId
        contactId
        decisionId
        decisionType
        decisionReason
        confidenceScore
        decidedAt
        scheduledFor
        executedAt
        completedAt
        flowExecutionId
        cadenceStep
        strategyId
        reasoning
        outcome
        outcomeTimestamp
        outcomeNotes
        createdAt
        updatedAt
        contactFirstName
        contactLastName
        contactPhone
        contactEmail
      }
      total
      hasMore
    }
  }
`;
